import { Flex, LoadingIcon } from "basis";
import { Layout } from "components/Layout/Layout";
import { Account } from "library/accounts/types";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { useLayout } from "library/hooks/useLayout";
import { checkFields } from "library/utils";
import { FC, useEffect, useReducer } from "react";
import { usePageTracking } from "react-event-tracker";
import { PAGES } from "./constants";
import { initialState, reducer } from "./reducer";
import { Success } from "./Success";
import { ThirdPartyAuthorityForm } from "./ThirdPartyAuthorityForm";
import {
  Action,
  ActionType,
  PageInfo,
  ScreenComponentType,
  ScreenState,
} from "./types";

const pageInfo: PageInfo = {
  [PAGES.FORM]: {
    heading: "Third party authority",
    screenComponent: ThirdPartyAuthorityForm,
  },
  [PAGES.SUCCESS]: {
    heading: "Third party authority request sent!",
    headingIcon: "success",
    screenComponent: Success,
  },
};

interface ScreenProps {
  screenComponent: ScreenComponentType;
  state: ScreenState;
  dispatch: React.Dispatch<Action>;
}

const ThirdPartyAuthorityScreen = ({
  screenComponent: ScreenComponent,
  state,
  dispatch,
}: ScreenProps) => {
  const { scrollToTop } = useLayout();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, ScreenComponent]);

  return <ScreenComponent state={state} dispatch={dispatch} />;
};

interface Props {
  location: {
    state?: {
      account: Account;
    };
  };
}

export const ThirdPartyAuthority: FC<Props> = ({ location }) => {
  const { account } = location.state || {};
  const accountInfo = useAccountInfo(account);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { page } = state;
  const {
    heading: pageHeading,
    headingIcon: pageHeadingIcon,
    screenComponent,
  } = pageInfo[page];

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "accounts",
      siteSubSection: "manage",
      pageTitle: "third-party-authority",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });

  useEffect(() => {
    if (accountInfo !== null) {
      dispatch({
        type: ActionType.ACCOUNT_INFO_LOADED,
        accountInfo,
      });
    }
  }, [accountInfo]);

  return (
    <Layout>
      <Layout.Header icon={pageHeadingIcon}>{pageHeading}</Layout.Header>
      <Layout.Main>
        {checkFields(state, "accountInfo") ? (
          <ThirdPartyAuthorityScreen
            screenComponent={screenComponent}
            state={state}
            dispatch={dispatch}
          />
        ) : (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        )}
      </Layout.Main>
    </Layout>
  );
};
